import React from "react";
import gql from "graphql-tag";
import produce from "immer";
import { useMutation } from "react-apollo";
import { Bugsnag } from "@services";
import { Button, Text } from "@atoms";
import { IGiftcardReloadState, TGiftcardReloadDispatch } from "./types";
import {
  GiftcardRefillButtonMutation,
  GiftcardRefillButtonMutationVariables,
} from "./__generated__/GiftcardRefillButtonMutation";

// == Types ================================================================

interface IProps {
  state: IGiftcardReloadState;
  dispatch: TGiftcardReloadDispatch;
}

// == Constants ============================================================

const MARK_ALL_READ_MUTATION = gql`
  mutation GiftcardRefillButtonMutation($input: GiftcardReloadInputType!) {
    ViewerGiftcardReload(input: $input) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on Giftcard {
        id
        isReloadable
        balance {
          dollarsAmount
          formatted
        }
        value {
          formatted
          dollarsAmount
        }
        reloadInfo {
          isReloadingEnabled
          reloadPercentage
          stripeCardId
        }
        campaign {
          id
          name
        }
      }
    }
  }
`;

// == Component ============================================================
export function GiftcardRefillButton({ state, dispatch }: IProps) {
  const [updateGiftcardMutation, { loading, error }] = useMutation<
    GiftcardRefillButtonMutation,
    GiftcardRefillButtonMutationVariables
  >(MARK_ALL_READ_MUTATION, {
    onCompleted(resp) {
      if (resp?.ViewerGiftcardReload.__typename !== "MutationError") {
        dispatch({ type: "SUCCESSFUL_SAVE" });
      } else {
        dispatch({ type: "FAILED_SAVE" });
      }
    },
  });
  const onSubmit = async () => {
    try {
      const input = produce({}, (draft) => {
        draft.giftcardId = state.giftcard.id;
        draft.campaignId = state.selectedCampaignId;
        draft.isNotificationAllowed = state.isNotificationAllowed;
        if (state.manualReloadDollarsAmount) draft.amount = state.manualReloadDollarsAmount;
        if (state.stripeCardId) draft.stripeCardId = state.stripeCardId;
        if (state.stripeTokenId) draft.stripeTokenId = state.stripeTokenId;
      });

      const resp = await updateGiftcardMutation({
        variables: {
          input,
        },
      });
      const { data } = resp;
      if (data.ViewerGiftcardReload.__typename === "MutationError") return;
      dispatch({
        type: "UPDATE_BALANCE",
        balanceFormatted: data?.ViewerGiftcardReload.balance?.formatted,
      });
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  return (
    <>
      <Button
        disabled={loading || state.manualReloadDollarsAmount === 0}
        width="100%"
        onClick={onSubmit}
      >
        {loading ? "Reloading..." : "Reload Now"}
      </Button>
      {(error || state.error) && (
        <Text color="danger" display="block" mt={1} textAlign="center">
          {state.error || error?.message}
        </Text>
      )}
    </>
  );
}
